// userPersonas.js
//
// Helper for retrieving the logged-in user's persona names.
// Flow:
//   1. Use the elli SDK to obtain the auth object and the basic user (for `id`).
//   2. Exchange an authCode for an access token.
//   3. Call GET /encompass/v3/externalUsers/{id} via elliemaeApi
//      (baseURL = REACT_APP_ICE_API_BASE_URL) to fetch the full user object.
//   4. Return the personas[].entityName array from the API response.

import { getAccessToken } from "../services/authApi";
import { getUserDetails } from "../services/userApi";
import { debug } from "./logger";

const extractPersonaNames = (personas) =>
    Array.isArray(personas)
        ? personas
            .map(p => String(p?.entityName || "").trim())
            .filter(Boolean)
        : [];

/**
 * Returns an array of persona entityName strings for the currently
 * logged-in user, sourced from the externalUsers API. Returns an
 * empty array if the SDK / token / API call fails.
 *
 * @returns {Promise<string[]>}
 */
export const getLoggedInUserPersonas = async () => {
    try {

        if (!window?.elli?.script?.getObject) {
            return [];
        }

        const authObj = await window.elli.script.getObject("auth");
        const sdkUser = await authObj?.getUser?.();
        const userId = sdkUser?.id;

        if (!userId) {
            console.warn("No user id available from elli auth.getUser().");
            return [];
        }

        const authCode = await authObj.createAuthCode();
        const token = await getAccessToken(authCode);
        if (!token) {
            console.warn("Failed to obtain access token for externalUsers call.");
            return [];
        }

        const apiUser = await getUserDetails(token, userId);
        var personas = extractPersonaNames(apiUser?.personas);
        // Add mocking personas here for testing without elli auth context, e.g.:
        //personas.push("Corr - Full Access w/o Pricing"); // <-- example persona for testing
        //personas=[];
        //personas.push("Corr - Pipeline & Resources"); // <-- example persona for testing
        //personas.push("Corr - Full Access"); // <-- example persona for testing
        return personas;

        // log the full user object for debugging (remove in production)
        console.debug("Full user object from externalUsers API:", apiUser);
    } catch (err) {
        console.warn("Failed to load logged-in user personas:", err);
        return [];
    }
};

